import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Globe,
  CheckCircle,
  ShieldCheck,
  Buildings,
  Storefront,
  Handshake,
  Certificate,
  Package,
  ChartLineUp,
  GraduationCap,
  ArrowRight,
  Sparkle,
  MapTrifold,
  Coins,
  Users,
} from '@phosphor-icons/react';
import Logo from '@/components/Logo';
import LanguageSwitcher from '@/components/LanguageSwitcher';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SECTORS = ['Moda & Giyim', 'Kozmetik & Kişisel Bakım', 'Ev & Yaşam', 'Elektronik', 'Gıda & Takviye', 'Mücevher & Aksesuar', 'Bebek & Çocuk', 'Otomotiv Aksesuar', 'El Sanatları', 'Diğer'];
const REVENUES = ['0-1M TL', '1-5M TL', '5-20M TL', '20-100M TL', '100M+ TL'];
const CURRENT_MARKETPLACES = ['Trendyol', 'Hepsiburada', 'N11', 'Amazon TR', 'Kendi web sitem', 'Henüz satmıyorum'];
const TARGET_MARKETS = ['Amazon US', 'Amazon EU (DE/FR/IT/ES/UK)', 'Amazon MENA (AE/SA)', 'Amazon Asya (JP)', 'Etsy', 'Walmart', 'Ozon (Rusya)', 'Otto (Almanya)', 'eBay', 'Kendi web sitem (Uluslararası)'];

const EIhracatConsultingPage = () => {
  const [form, setForm] = useState({
    full_name: '', email: '', phone: '', company_name: '', website: '',
    sector: '', annual_revenue: '',
    current_marketplaces: [], target_markets: [],
    goals: '', consent_marketing: true,
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    document.title = 'E-İhracat Danışmanlığı — 2 Yıllık Uygulamalı Deneyim · GrowthRIQ';
    const setMeta = (name, value) => {
      let el = document.head.querySelector(`meta[name="${name}"]`);
      if (!el) { el = document.createElement('meta'); el.setAttribute('name', name); document.head.appendChild(el); }
      el.setAttribute('content', value);
    };
    setMeta('description', '2 yılda 30+ firma ile uygulamalı e-ihracat projesi yürüttük. Amazon 17 ülke, Etsy, Walmart, Ozon, Otto. Ücretsiz analizle firmanızın hazırlık seviyesini ölçün.');
    setMeta('keywords', 'e-ihracat danışmanlığı, Amazon satıcı danışmanı, Etsy danışmanlığı, KOSGEB destek, e-ihracat eğitimi, uluslararası pazaryeri danışmanlığı');

    // GTM datalayer view event for ads attribution
    if (typeof window !== 'undefined') {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: 'view_consulting_page', page: 'e-ihracat-danismanlik' });
    }
  }, []);

  const toggleMulti = (key, val) => {
    setForm((f) => {
      const cur = f[key];
      return { ...f, [key]: cur.includes(val) ? cur.filter((x) => x !== val) : [...cur, val] };
    });
  };

  const scrollToForm = () => {
    document.getElementById('consulting-form')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.full_name.trim() || !form.email.trim()) {
      toast.error('Ad soyad ve e-posta zorunlu');
      return;
    }
    setSubmitting(true);
    try {
      const urlParams = new URLSearchParams(window.location.search);
      const payload = {
        ...form,
        source: urlParams.get('utm_source') || 'organic',
        utm_medium: urlParams.get('utm_medium') || undefined,
        utm_campaign: urlParams.get('utm_campaign') || undefined,
      };
      const res = await axios.post(`${API_URL}/consulting/leads`, payload);
      if (res.data?.ok) {
        setSubmitted(true);
        toast.success('Talebiniz alındı. En kısa sürede size ulaşacağız.');
        if (typeof window !== 'undefined') {
          window.dataLayer = window.dataLayer || [];
          window.dataLayer.push({ event: 'consulting_lead_submit', lead_id: res.data.id });
        }
      }
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Bir hata oluştu, lütfen tekrar deneyin.';
      toast.error(typeof detail === 'string' ? detail : 'Form gönderilemedi');
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls = 'w-full px-4 py-3 border border-[#E4E4E7] rounded-md focus:outline-none focus:border-[#0F1B36] text-[#09090B] text-sm bg-white';
  const labelCls = 'block text-sm font-semibold text-[#0F1B36] mb-2';

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="border-b border-[#E4E4E7] sticky top-0 bg-white/95 backdrop-blur z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2"><Logo variant="light" size="md" /></Link>
          <div className="flex items-center gap-4">
            <LanguageSwitcher />
            <Link to="/register" className="btn-primary" data-testid="nav-register-link">Ücretsiz Değerlendirme</Link>
          </div>
        </div>
      </nav>

      {/* HERO */}
      <section className="py-20 px-6 border-b border-[#E4E4E7] bg-gradient-to-br from-white via-[#F8FAFB] to-white">
        <div className="max-w-7xl mx-auto grid md:grid-cols-12 gap-12 items-center">
          <div className="md:col-span-7">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#3CD9B8]/10 rounded-full mb-5">
              <Certificate size={16} weight="fill" className="text-[#0F1B36]" />
              <span className="text-xs font-bold text-[#0F1B36] tracking-wider uppercase">2 Yıllık Uygulamalı E-İhracat Deneyimi</span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-[#0F1B36] mb-4 leading-tight" data-testid="consulting-hero-title">
              Türkiye&apos;den Dünyaya. <span className="text-[#3CD9B8]">Rastgele değil,</span> yol haritanızla.
            </h1>
            <p className="text-xl text-[#52525B] mb-8 leading-relaxed max-w-2xl">
              <strong>2 yılda 30+ firma</strong> ile uygulamalı e-ihracat projesi yürüttük.
              Amazon <strong>17 ülke</strong>, Etsy, Walmart, Ozon ve Otto başta olmak üzere <strong>onlarca uluslararası pazaryerinde</strong> firmalara uçtan-uca danışmanlık veriyoruz.
            </p>
            <div className="flex flex-wrap gap-4 mb-8">
              <button onClick={scrollToForm} className="btn-primary inline-flex items-center gap-2" data-testid="hero-cta-form">
                Ücretsiz Ön Görüşme Al <ArrowRight size={20} weight="bold" />
              </button>
              <Link to="/register" className="btn-secondary inline-flex items-center gap-2" data-testid="hero-cta-assessment">
                Önce olgunluk testini yap <Sparkle size={18} weight="bold" />
              </Link>
            </div>
            <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm text-[#52525B]">
              <span className="inline-flex items-center gap-2"><CheckCircle size={16} weight="fill" className="text-[#3CD9B8]" /> Firma+pazar özel yol haritası</span>
              <span className="inline-flex items-center gap-2"><CheckCircle size={16} weight="fill" className="text-[#3CD9B8]" /> Devlet destekleri rehberliği</span>
              <span className="inline-flex items-center gap-2"><CheckCircle size={16} weight="fill" className="text-[#3CD9B8]" /> Birebir + saha uygulamalı</span>
            </div>
          </div>
          <div className="md:col-span-5">
            <div className="bg-[#0F1B36] text-white p-8 rounded-2xl shadow-2xl">
              <p className="text-xs font-bold text-[#3CD9B8] tracking-widest uppercase mb-2">Rakamlarla</p>
              <div className="space-y-5">
                <div>
                  <div className="text-4xl font-black">30+</div>
                  <div className="text-sm text-white/70">Uçtan uca projesi biten firma</div>
                </div>
                <div>
                  <div className="text-4xl font-black">17</div>
                  <div className="text-sm text-white/70">Amazon aktif ülke pazarı</div>
                </div>
                <div>
                  <div className="text-4xl font-black">10+</div>
                  <div className="text-sm text-white/70">Aktif danışmanlık firması</div>
                </div>
                <div>
                  <div className="text-4xl font-black">5</div>
                  <div className="text-sm text-white/70">Farklı sektör deneyimi</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* NEDEN BİZ */}
      <section className="py-20 px-6 border-b border-[#E4E4E7]">
        <div className="max-w-7xl mx-auto">
          <div className="mb-12 max-w-3xl">
            <p className="text-xs font-bold text-[#3CD9B8] tracking-widest uppercase mb-3">Neden Datinova E-İhracat</p>
            <h2 className="text-3xl sm:text-4xl font-black text-[#0F1B36] mb-4">Her firmaya {'"aynı paket"'} değil. <span className="text-[#3CD9B8]">Size özel yol haritası.</span></h2>
            <p className="text-lg text-[#52525B] leading-relaxed">
              Danışmanlığa başlamadan <strong>onaylı e-ticaret &amp; e-ihracat olgunluk aracımızla</strong> firmanızın mevcut seviyesini ölçüyoruz. Ürününüz, sektörünüz, hedef pazarınız ve kaynaklarınıza göre — 30/60/90 gün için gerçekçi bir hazırlık planı çıkarıyoruz. Genel bir e-ihracat {'"kursu"'} değil, sizin firmanızın kendi durumuna göre birebir çalışma.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {[
              { icon: <MapTrifold size={28} weight="bold" />, title: 'Onaylı olgunluk aracı', desc: 'Her firma için AI destekli 10 kategoride skor karnesi. Nerede güçlüsünüz, nerede eksikliğiniz var — objektif tespit.' },
              { icon: <Handshake size={28} weight="bold" />, title: 'Birebir + saha uygulamalı', desc: 'Online eğitim + yüz yüze saha ziyareti + firmayla birlikte pazaryeri hesabı açılışı. Kağıt üzerinde değil, ekibinizle birlikte.' },
              { icon: <Coins size={28} weight="bold" />, title: 'Devlet destekleri süreçleri', desc: 'İhracatçı Birlikleri, KOSGEB ve Ticaret Bakanlığı destek programlarında başvuru, dosya hazırlama ve süreç yönetimi.' },
              { icon: <Storefront size={28} weight="bold" />, title: 'Global pazaryeri uzmanlığı', desc: 'Amazon 17 ülke (US/UK/DE/FR/IT/ES/CA/MX/JP/AE/SA/EG/TR + Avustralya/Hindistan/Singapur/Brezilya), Etsy, Walmart, Ozon, Otto, eBay.' },
              { icon: <Package size={28} weight="bold" />, title: 'Uçtan uca operasyon', desc: 'Ürün seçiminden lojistiğe, ödeme sistemlerinden reklam yönetimine kadar 8 aylık süreçte yanınızda.' },
              { icon: <ChartLineUp size={28} weight="bold" />, title: 'Sürdürülebilir büyüme', desc: 'Sadece "açılış" değil — hesap sağlığı, review yönetimi, reklam optimizasyonu ile 12+ ay büyüme partnerliği.' },
            ].map((it, i) => (
              <div key={i} className="p-6 border border-[#E4E4E7] rounded-lg hover:border-[#3CD9B8] hover:shadow-md transition-all bg-white" data-testid={`why-card-${i}`}>
                <div className="w-12 h-12 bg-[#0F1B36] text-white flex items-center justify-center rounded-lg mb-4">{it.icon}</div>
                <h3 className="text-lg font-bold text-[#0F1B36] mb-2">{it.title}</h3>
                <p className="text-sm text-[#52525B] leading-relaxed">{it.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* GLOBAL PAZARYERLERİ */}
      <section className="py-20 px-6 border-b border-[#E4E4E7] bg-[#0F1B36] text-white">
        <div className="max-w-7xl mx-auto">
          <div className="mb-12 max-w-3xl">
            <p className="text-xs font-bold text-[#3CD9B8] tracking-widest uppercase mb-3">Global Pazaryeri Uzmanlığı</p>
            <h2 className="text-3xl sm:text-4xl font-black mb-4">Sadece Amazon değil. <span className="text-[#3CD9B8]">Doğru pazar</span> için doğru platform.</h2>
            <p className="text-lg text-white/70 leading-relaxed">
              Her pazaryerinin kendine has bir müşteri profili, komisyon yapısı ve rekabet dinamiği var. Ürününüze göre nerede yüksek kâr, nerede yüksek hacim var — verilerle konuşup en uygun kombinasyonu birlikte belirliyoruz.
            </p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
            {[
              { name: 'Amazon', sub: '17 ülke', tag: 'ANA UZMANLIK' },
              { name: 'Etsy', sub: 'Handmade & Vintage', tag: '' },
              { name: 'Walmart', sub: 'USA', tag: '' },
              { name: 'Ozon', sub: 'Rusya & BDT', tag: '' },
              { name: 'Otto', sub: 'Almanya', tag: '' },
              { name: 'eBay', sub: 'Global', tag: '' },
              { name: 'Cdiscount', sub: 'Fransa', tag: '' },
              { name: 'Allegro', sub: 'Polonya', tag: '' },
              { name: 'Kaufland', sub: 'DACH', tag: '' },
              { name: 'Rakuten', sub: 'Japonya', tag: '' },
            ].map((m) => (
              <div key={m.name} className="p-4 bg-white/5 border border-white/10 rounded-lg hover:bg-white/10 transition-colors min-h-[110px] flex flex-col justify-between">
                <div>
                  <div className="text-lg font-bold leading-tight">{m.name}</div>
                  <div className="text-xs text-white/60 mt-1">{m.sub}</div>
                </div>
                {m.tag && <div className="text-[10px] font-bold text-[#3CD9B8] tracking-widest mt-2">{m.tag}</div>}
              </div>
            ))}
          </div>

          <p className="text-sm text-white/60 mt-8 max-w-3xl">
            + Kendi uluslararası web siteniz için Shopify / WooCommerce entegrasyonu, uluslararası ödeme altyapısı (Stripe / PayPal / Payoneer) ve lojistik ağı kurulumu.
          </p>
        </div>
      </section>

      {/* METODOLOJI - 8 aylık akış */}
      <section className="py-20 px-6 border-b border-[#E4E4E7]">
        <div className="max-w-7xl mx-auto">
          <div className="mb-12 max-w-3xl">
            <p className="text-xs font-bold text-[#3CD9B8] tracking-widest uppercase mb-3">Metodolojimiz</p>
            <h2 className="text-3xl sm:text-4xl font-black text-[#0F1B36] mb-4">8 Aylık Uçtan Uca Program</h2>
            <p className="text-lg text-[#52525B] leading-relaxed">
              Rastgele {'"önce hesap aç bakalım ne olacak"'} değil. Geçmiş projelerimizde 30+ firma ile test edilmiş, adım adım proje planı:
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { m: '1. AY', t: 'Analiz & Eğitim', d: '360° e-ihracat eğitimi + status ve marka başvuruları + firma içi süreç uyumu.' },
              { m: '2. AY', t: 'Pazar & Ürün Analizi', d: 'Hangi ülke, hangi ürün, hangi rakip — data odaklı seçim (Helium 10, Jungle Scout, Similarweb).' },
              { m: '3. AY', t: 'Hesap Açılışı & Ödeme', d: 'Pazaryeri onboarding + Payoneer/Stripe entegrasyonu + Türkiye hesap bağlantısı.' },
              { m: '4. AY', t: 'Ürün Hazırlığı', d: 'Ürün seçimi, ambalaj + barkod, ETGB, lojistik ortağı seçimi.' },
              { m: '5. AY', t: 'Listeleme', d: 'A+ Content, görsel çekim, SEO optimizasyonu, çoklu dil çevirileri.' },
              { m: '6. AY', t: 'Reklam & Lansman', d: 'PPC, sponsored products, external traffic, retargeting kampanyaları.' },
              { m: '7. AY', t: 'Satış Karşılama', d: 'Sipariş süreçleri, müşteri desteği, review yönetimi, iade lojistiği.' },
              { m: '8. AY', t: 'Sürdürülebilirlik', d: 'Hesap sağlığı takibi, aylık optimizasyon raporu, ölçeklendirme planı.' },
            ].map((s, i) => (
              <div key={i} className="p-5 border-l-4 border-[#3CD9B8] bg-[#FAFAFA]">
                <div className="text-xs font-bold text-[#3CD9B8] tracking-wider mb-1">{s.m}</div>
                <div className="text-lg font-bold text-[#0F1B36] mb-2">{s.t}</div>
                <p className="text-sm text-[#52525B] leading-relaxed">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* DEVLET DESTEKLERİ */}
      <section className="py-20 px-6 border-b border-[#E4E4E7] bg-[#FAFAFA]">
        <div className="max-w-7xl mx-auto grid md:grid-cols-2 gap-12 items-center">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-white border border-[#3CD9B8]/30 rounded-full mb-5">
              <Coins size={16} weight="fill" className="text-[#0F1B36]" />
              <span className="text-xs font-bold text-[#0F1B36] tracking-wider uppercase">%50-70 Devlet Desteği</span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-black text-[#0F1B36] mb-4">Süreç kadar destek dosyalarını da biz yönetiyoruz</h2>
            <p className="text-lg text-[#52525B] mb-6 leading-relaxed">
              KOSGEB, İhracatçı Birlikleri ve Ticaret Bakanlığı programları e-ihracat maliyetlerinizin büyük bir kısmını karşılıyor.
              <strong> Ama başvuru + belgelendirme + hakediş süreçleri karmaşık.</strong> 30+ firmanın destek dosyalarını uygulamalı olarak yönettik — hangi kalemin hangi programa uygun olduğunu, hangi belgenin gerektiğini adım adım biliyoruz.
            </p>
            <ul className="space-y-3">
              {[
                'Pazaryeri komisyonları (Amazon, Etsy, Walmart) destek dosyası',
                'Reklam giderleri (PPC, Facebook, Google Ads) destek başvurusu',
                'Yurtdışı depo & lojistik giderleri',
                'Marka tescili (yurtiçi + yurtdışı) desteği',
                'Uluslararası fuar katılım destekleri',
                'Danışmanlık gideri destek belgeleri',
              ].map((d) => (
                <li key={d} className="flex items-start gap-2 text-[#52525B]">
                  <CheckCircle size={20} weight="fill" className="text-[#3CD9B8] flex-shrink-0 mt-0.5" />
                  <span>{d}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="bg-white p-8 rounded-2xl border border-[#E4E4E7] shadow-sm">
            <div className="flex items-center gap-3 mb-4">
              <GraduationCap size={32} weight="bold" className="text-[#0F1B36]" />
              <div>
                <div className="text-sm font-bold text-[#0F1B36]">Bilinen destek programları</div>
                <div className="text-xs text-[#71717A]">Ekibimizin uygulamalı deneyimi olan</div>
              </div>
            </div>
            <div className="space-y-3 text-sm">
              {[
                { k: 'İhracatçı Birlikleri', v: 'Pazara giriş destekleri ve fuar katılım programları' },
                { k: 'Ticaret Bakanlığı', v: 'Turquality, Marka Destek Programı, E-İhracat Desteği' },
                { k: 'KOSGEB', v: 'Yurtdışı pazar hazırlığı ve tanıtım desteği' },
                { k: 'TİM', v: 'Türkiye İhracatçılar Meclisi hibe programları' },
              ].map((it) => (
                <div key={it.k} className="p-3 bg-[#FAFAFA] rounded-lg">
                  <div className="font-bold text-[#0F1B36]">{it.k}</div>
                  <div className="text-[#52525B]">{it.v}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* LEAD FORM */}
      <section id="consulting-form" className="py-20 px-6 bg-[#09090B] text-white">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-10">
            <p className="text-xs font-bold text-[#3CD9B8] tracking-widest uppercase mb-3">Ücretsiz Ön Görüşme</p>
            <h2 className="text-3xl sm:text-4xl font-black mb-4">Firmanıza özel yol haritası için 15 dakika ayırın</h2>
            <p className="text-lg text-white/70 leading-relaxed max-w-2xl mx-auto">
              Formu doldurun, <strong>24 saat içinde</strong> uzman ekibimiz sizi arasın. Ücretsiz ön görüşmede firmanızın mevcut durumuna göre <strong>hangi pazaryerinden başlanması gerektiği ve hangi devlet desteklerinin size uygun olduğunu</strong> konuşalım.
            </p>
          </div>

          {submitted ? (
            <div className="bg-white text-[#0F1B36] p-10 rounded-2xl text-center" data-testid="consulting-form-success">
              <CheckCircle size={64} weight="fill" className="text-[#3CD9B8] mx-auto mb-4" />
              <h3 className="text-2xl font-bold mb-2">Talebiniz Alındı!</h3>
              <p className="text-[#52525B] mb-6">En kısa sürede uzman ekibimiz sizinle iletişime geçecek. Bu arada, sistemimizdeki ücretsiz olgunluk aracını kullanarak firmanızın hazırlık seviyesini şimdiden ölçebilirsiniz:</p>
              <div className="flex flex-wrap gap-3 justify-center">
                <Link to="/register" className="btn-primary inline-flex items-center gap-2">
                  Ücretsiz Olgunluk Testine Başla <ArrowRight size={18} weight="bold" />
                </Link>
                <Link to="/demo" className="btn-secondary">Örnek Raporu İncele</Link>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="bg-white text-[#0F1B36] p-8 rounded-2xl" data-testid="consulting-form">
              <div className="grid md:grid-cols-2 gap-5 mb-5">
                <div>
                  <label className={labelCls}>Ad Soyad <span className="text-red-500">*</span></label>
                  <input type="text" required value={form.full_name} onChange={(e) => setForm({...form, full_name: e.target.value})} className={inputCls} data-testid="consulting-input-name" />
                </div>
                <div>
                  <label className={labelCls}>E-posta <span className="text-red-500">*</span></label>
                  <input type="email" required value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} className={inputCls} data-testid="consulting-input-email" />
                </div>
                <div>
                  <label className={labelCls}>Telefon</label>
                  <input type="tel" placeholder="+90 5xx xxx xx xx" value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})} className={inputCls} data-testid="consulting-input-phone" />
                </div>
                <div>
                  <label className={labelCls}>Şirket Adı</label>
                  <input type="text" value={form.company_name} onChange={(e) => setForm({...form, company_name: e.target.value})} className={inputCls} data-testid="consulting-input-company" />
                </div>
                <div>
                  <label className={labelCls}>Web Sitesi</label>
                  <input type="text" placeholder="https://..." value={form.website} onChange={(e) => setForm({...form, website: e.target.value})} className={inputCls} data-testid="consulting-input-website" />
                </div>
                <div>
                  <label className={labelCls}>Sektör</label>
                  <select value={form.sector} onChange={(e) => setForm({...form, sector: e.target.value})} className={inputCls} data-testid="consulting-input-sector">
                    <option value="">Seçiniz…</option>
                    {SECTORS.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
              </div>

              <div className="mb-5">
                <label className={labelCls}>Yıllık Ciro Aralığı</label>
                <div className="flex flex-wrap gap-2">
                  {REVENUES.map((r) => (
                    <button type="button" key={r} onClick={() => setForm({...form, annual_revenue: r})}
                      className={`px-4 py-2 border rounded-full text-sm font-medium transition-all ${form.annual_revenue === r ? 'bg-[#0F1B36] border-[#0F1B36] text-white' : 'bg-white border-[#E4E4E7] text-[#52525B] hover:border-[#0F1B36]'}`}
                      data-testid={`consulting-revenue-${r.replace(/[^a-zA-Z0-9]/g, '')}`}>
                      {r}
                    </button>
                  ))}
                </div>
              </div>

              <div className="mb-5">
                <label className={labelCls}>Şu an nerede satıyorsunuz? <span className="text-xs font-normal text-[#71717A]">(birden fazla seçebilirsiniz)</span></label>
                <div className="flex flex-wrap gap-2">
                  {CURRENT_MARKETPLACES.map((m) => (
                    <button type="button" key={m} onClick={() => toggleMulti('current_marketplaces', m)}
                      className={`px-4 py-2 border rounded-full text-sm font-medium transition-all ${form.current_marketplaces.includes(m) ? 'bg-[#3CD9B8]/10 border-[#3CD9B8] text-[#0F1B36]' : 'bg-white border-[#E4E4E7] text-[#52525B] hover:border-[#0F1B36]'}`}>
                      {form.current_marketplaces.includes(m) && '✓ '}{m}
                    </button>
                  ))}
                </div>
              </div>

              <div className="mb-5">
                <label className={labelCls}>Hangi pazarlara açılmak istiyorsunuz? <span className="text-xs font-normal text-[#71717A]">(birden fazla seçebilirsiniz)</span></label>
                <div className="flex flex-wrap gap-2">
                  {TARGET_MARKETS.map((m) => (
                    <button type="button" key={m} onClick={() => toggleMulti('target_markets', m)}
                      className={`px-4 py-2 border rounded-full text-sm font-medium transition-all ${form.target_markets.includes(m) ? 'bg-[#3CD9B8]/10 border-[#3CD9B8] text-[#0F1B36]' : 'bg-white border-[#E4E4E7] text-[#52525B] hover:border-[#0F1B36]'}`}>
                      {form.target_markets.includes(m) && '✓ '}{m}
                    </button>
                  ))}
                </div>
              </div>

              <div className="mb-5">
                <label className={labelCls}>Hedefiniz / Kısa Bilgi</label>
                <textarea rows={4} value={form.goals} onChange={(e) => setForm({...form, goals: e.target.value})} className={inputCls}
                  placeholder="Ör: El yapımı takılarımızı Etsy&apos;de satmayı planlıyoruz. Ayda 50k USD ciro hedefliyoruz. Depo, kargo ve marka tescili konusunda destek almak istiyoruz."
                  data-testid="consulting-input-goals" />
              </div>

              <div className="mb-6">
                <label className="flex items-start gap-2 cursor-pointer">
                  <input type="checkbox" checked={form.consent_marketing} onChange={(e) => setForm({...form, consent_marketing: e.target.checked})} className="mt-1" data-testid="consulting-consent-checkbox" />
                  <span className="text-xs text-[#52525B] leading-relaxed">
                    Datinova&apos;nın benimle e-posta, telefon veya WhatsApp yoluyla iletişime geçmesine izin veriyorum. <Link to="/legal/kvkk" className="text-[#3CD9B8] underline">KVKK Aydınlatma Metni</Link>
                  </span>
                </label>
              </div>

              <button type="submit" disabled={submitting} className="btn-primary w-full py-4 text-lg inline-flex items-center justify-center gap-2 disabled:opacity-60" data-testid="consulting-submit-btn">
                {submitting ? 'Gönderiliyor…' : 'Ücretsiz Ön Görüşme Talep Et'}
                {!submitting && <ArrowRight size={20} weight="bold" />}
              </button>
              <p className="text-xs text-center text-[#71717A] mt-3">
                24 saat içinde uzman ekibimiz size dönecek. <ShieldCheck size={12} className="inline" /> Bilgileriniz gizli tutulur.
              </p>
            </form>
          )}
        </div>
      </section>

      {/* BOTTOM CTA — assessment */}
      <section className="py-16 px-6 bg-[#FAFAFA] border-t border-[#E4E4E7]">
        <div className="max-w-4xl mx-auto text-center">
          <Sparkle size={40} weight="fill" className="text-[#3CD9B8] mx-auto mb-4" />
          <h2 className="text-2xl sm:text-3xl font-black text-[#0F1B36] mb-3">Ön görüşme öncesi hazırlık ister misiniz?</h2>
          <p className="text-lg text-[#52525B] mb-6 max-w-2xl mx-auto">
            Sistemdeki <strong>AI destekli olgunluk aracımızı</strong> 5 dakikada tamamlayın — firmanızın e-ihracata hazırlık skoru, SWOT analizi ve 30/60/90 gün aksiyon planı otomatik oluşturulsun.
          </p>
          <Link to="/register" className="btn-primary inline-flex items-center gap-2" data-testid="bottom-cta-assessment">
            Ücretsiz Olgunluk Testine Başla <ArrowRight size={20} weight="bold" />
          </Link>
        </div>
      </section>

      {/* Footer minimum */}
      <footer className="py-8 px-6 border-t border-[#E4E4E7] bg-white">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-[#52525B]">
          <div>© 2026 Datinova Eğitim Danışmanlık ve Yazılım Ltd. Şti. — GrowthRIQ markası ile faaliyet gösterir.</div>
          <div className="flex gap-4">
            <Link to="/legal/kvkk" className="hover:text-[#0F1B36]">KVKK</Link>
            <Link to="/legal/gizlilik" className="hover:text-[#0F1B36]">Gizlilik</Link>
            <Link to="/iletisim" className="hover:text-[#0F1B36]">İletişim</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default EIhracatConsultingPage;
